import { useTranslation } from 'react-i18next';
import { receiptRows, receiptSummary } from '../../lib/answerReceipt';
import type { RequestViewContext } from '../../lib/viewContext';
import type { AnswerContextReceipt, CountReceipt } from '../../types';
import { Icon } from '../shared/Icon';
import { CountReceiptLine } from './CountReceiptLine';

/**
 * Every answer's receipt (Phase C), extending the count receipts: collapsed,
 * one line of what the tools read ("Counted: Seefeld · 14 Sept – 11 Oct 2026
 * · vs … · Statuses: Done · all sources · all shifts"); open, one row per
 * part with what didn't apply, the definitions of the figures and the count
 * lines underneath. An answer from before Phase C shows its count lines only.
 */
export function AnswerReceipt({
  receipt,
  receipts,
  context,
}: {
  receipt?: AnswerContextReceipt | null;
  receipts?: CountReceipt[] | null;
  context?: RequestViewContext | null;
}) {
  const { t } = useTranslation('assistant');
  if (!receipt) return <CountReceiptLine receipts={receipts} />;
  const rows = receiptRows(receipt, context);
  const summary = receiptSummary(rows);
  return (
    <details className="answer-receipt">
      <summary aria-label={`${t('receipt.aria')}: ${summary.text}`}>
        <Icon name="check" size={12} />
        <span className="ar-title">{t('receipt.title')}</span>
        <span className="ar-summary">{summary.text}</span>
        {summary.flagged ? (
          <span className="ar-flag">{t('receipt.flagged', { count: summary.flagged })}</span>
        ) : null}
        {summary.outside ? <span className="ar-flag">{t('receipt.outsideFlag')}</span> : null}
        <Icon name="chevron" size={12} className="ar-chevron" />
      </summary>
      <dl className="ar-rows">
        {rows.map((row, index) => (
          <div key={`${row.key}-${index}`} className={`ar-row${row.flag ? ' is-flagged' : ''}`}>
            <dt>{row.label}</dt>
            <dd>
              <span className="ar-value">{row.value}</span>
              {row.note ? <span className="ar-note">{row.note}</span> : null}
            </dd>
          </div>
        ))}
      </dl>
      {receipts?.length ? (
        <div className="ar-counts">
          <CountReceiptLine receipts={receipts} />
        </div>
      ) : null}
    </details>
  );
}
